import { useEffect, useState, useCallback } from 'react';
import { Badge } from '../components/ui/badge';
import {
  Clock, Phone, Calendar, X, RotateCcw, Trash2, ChevronRight,
  CheckCircle, AlertTriangle, Pause, PhoneOff, Search, Filter,
} from 'lucide-react';
import { useScheduleRecommendations, ScheduleSuggestionsCard } from '../components/ScheduleSuggestions';

const API = process.env.REACT_APP_BACKEND_URL;

const PURPOSE_LABELS = {
  follow_up: 'Follow-Up',
  showing_reminder: 'Showing Reminder',
  new_lead_outreach: 'New Lead Outreach',
  seller_follow_up: 'Seller Follow-Up',
  custom: 'Custom',
};

const PURPOSE_COLORS = {
  follow_up: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  showing_reminder: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  new_lead_outreach: 'bg-violet-500/10 text-violet-400 border-violet-500/20',
  seller_follow_up: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  custom: 'bg-secondary text-muted-foreground border-border',
};

const STATUS_STYLES = {
  pending: { icon: Clock, color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20', label: 'Pending' },
  executing: { icon: Phone, color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20 animate-pulse', label: 'Executing' },
  completed: { icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20', label: 'Completed' },
  failed: { icon: AlertTriangle, color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/20', label: 'Failed' },
  cancelled: { icon: X, color: 'text-muted-foreground', bg: 'bg-secondary border-border', label: 'Cancelled' },
  deferred: { icon: Pause, color: 'text-orange-400', bg: 'bg-orange-500/10 border-orange-500/20', label: 'Deferred' },
};

const STATUS_FILTERS = ['all', 'pending', 'deferred', 'completed', 'failed', 'cancelled'];

export default function ScheduledCallsPage() {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQ, setSearchQ] = useState('');
  const [rescheduleId, setRescheduleId] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');

  const token = localStorage.getItem('auth_token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const { recs } = useScheduleRecommendations(token);

  const fetchCalls = useCallback(async () => {
    try {
      const statusQ = filter !== 'all' ? `?status=${filter}` : '';
      const res = await fetch(`${API}/api/scheduled-calls${statusQ}`, { headers });
      if (res.ok) setCalls(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchCalls(); }, [fetchCalls]);

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this scheduled call?')) return;
    await fetch(`${API}/api/scheduled-calls/${id}/cancel`, { method: 'POST', headers });
    fetchCalls();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this scheduled call?')) return;
    await fetch(`${API}/api/scheduled-calls/${id}`, { method: 'DELETE', headers });
    fetchCalls();
  };

  const handleReschedule = async (id) => {
    if (!rescheduleDate || !rescheduleTime) return;
    const isoDate = new Date(`${rescheduleDate}T${rescheduleTime}`).toISOString();
    await fetch(`${API}/api/scheduled-calls/${id}`, {
      method: 'PATCH', headers,
      body: JSON.stringify({ scheduled_at: isoDate }),
    });
    setRescheduleId(null);
    setRescheduleDate('');
    setRescheduleTime('');
    fetchCalls();
  };

  const filtered = calls.filter(c => {
    if (searchQ) {
      const q = searchQ.toLowerCase();
      if (!(c.lead_name || '').toLowerCase().includes(q) && !(c.lead_phone || '').includes(q)) return false;
    }
    return true;
  });

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
      });
    } catch { return iso; }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64" data-testid="scheduled-calls-loading">
      <div className="h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto px-4 py-6" data-testid="scheduled-calls-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-slate-100">Scheduled Calls</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{filtered.length} scheduled call{filtered.length !== 1 ? 's' : ''}</p>
        </div>
      </div>

      {/* Smart Scheduling Suggestions */}
      {recs && (
        <div className="mb-4">
          <ScheduleSuggestionsCard recs={recs} />
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by lead name or phone..."
            value={searchQ}
            onChange={e => setSearchQ(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-muted border border-border rounded-lg text-sm text-foreground placeholder-slate-500 focus:outline-none focus:border-blue-500/50"
            data-testid="search-scheduled-calls"
          />
        </div>
        <div className="flex items-center gap-1">
          <Filter size={13} className="text-muted-foreground mr-1" />
          {STATUS_FILTERS.map(s => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-2.5 py-1 rounded-md text-xs capitalize transition-colors ${
                filter === s
                  ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                  : 'text-muted-foreground hover:text-foreground border border-transparent'
              }`}
              data-testid={`filter-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground" data-testid="no-scheduled-calls">
          <PhoneOff size={32} className="mx-auto mb-3 opacity-40" />
          <p className="text-sm">No scheduled calls found.</p>
        </div>
      ) : (
        <div className="border border-border rounded-xl overflow-hidden">
          <table className="w-full" data-testid="scheduled-calls-table">
            <thead>
              <tr className="text-xs text-muted-foreground uppercase tracking-wider border-b border-border bg-muted">
                <th className="text-left px-4 py-2.5">Lead</th>
                <th className="text-left px-4 py-2.5">Purpose</th>
                <th className="text-left px-4 py-2.5">Scheduled For</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-left px-4 py-2.5">Outcome</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => {
                const st = STATUS_STYLES[c.status] || STATUS_STYLES.pending;
                const StIcon = st.icon;
                const canAct = c.status === 'pending' || c.status === 'deferred';
                return (
                  <tr
                    key={c.id}
                    className="border-b border-border/30 hover:bg-muted transition-colors"
                    data-testid={`scheduled-row-${c.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="text-sm text-foreground font-medium">{c.lead_name || 'Unknown'}</div>
                      <div className="text-xs text-muted-foreground">{c.lead_phone}</div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={`text-xs ${PURPOSE_COLORS[c.purpose] || PURPOSE_COLORS.custom}`} data-testid={`purpose-badge-${c.id}`}>
                        {PURPOSE_LABELS[c.purpose] || c.purpose}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-foreground">{fmtDate(c.scheduled_at)}</div>
                      {c.deferred_reason && (
                        <div className="text-[10px] text-orange-400 mt-0.5">{c.deferred_reason}</div>
                      )}
                      {c.retry_count > 0 && (
                        <div className="text-[10px] text-muted-foreground mt-0.5">Retry {c.retry_count}/{c.max_retries || 2}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={`text-xs ${st.bg}`} data-testid={`status-badge-${c.id}`}>
                        <StIcon size={10} className={`mr-1 inline ${st.color}`} />{st.label}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      {c.result_outcome ? (
                        <span className="text-xs text-muted-foreground">{c.result_outcome}</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                      {c.result_summary && (
                        <div className="text-[10px] text-muted-foreground mt-0.5 line-clamp-1 max-w-[180px]">{c.result_summary}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {canAct && rescheduleId === c.id ? (
                          <div className="flex items-center gap-1.5">
                            <input
                              type="date"
                              value={rescheduleDate}
                              onChange={e => setRescheduleDate(e.target.value)}
                              className="px-1.5 py-1 bg-muted border border-border rounded text-xs text-foreground w-[110px]"
                              data-testid={`reschedule-date-${c.id}`}
                            />
                            <input
                              type="time"
                              value={rescheduleTime}
                              onChange={e => setRescheduleTime(e.target.value)}
                              className="px-1.5 py-1 bg-muted border border-border rounded text-xs text-foreground w-[80px]"
                              data-testid={`reschedule-time-${c.id}`}
                            />
                            <button
                              onClick={() => handleReschedule(c.id)}
                              className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs hover:bg-blue-500/30"
                              data-testid={`reschedule-confirm-${c.id}`}
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setRescheduleId(null)}
                              className="p-1 text-muted-foreground hover:text-foreground"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ) : (
                          <>
                            {canAct && (
                              <>
                                <button
                                  onClick={() => setRescheduleId(c.id)}
                                  className="p-1.5 text-muted-foreground hover:text-blue-400 hover:bg-blue-500/10 rounded transition-colors"
                                  title="Reschedule"
                                  data-testid={`reschedule-btn-${c.id}`}
                                >
                                  <RotateCcw size={13} />
                                </button>
                                <button
                                  onClick={() => handleCancel(c.id)}
                                  className="p-1.5 text-muted-foreground hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                                  title="Cancel"
                                  data-testid={`cancel-btn-${c.id}`}
                                >
                                  <X size={13} />
                                </button>
                              </>
                            )}
                            {(c.status === 'cancelled' || c.status === 'pending' || c.status === 'deferred') && (
                              <button
                                onClick={() => handleDelete(c.id)}
                                className="p-1.5 text-muted-foreground hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                                title="Delete"
                                data-testid={`delete-btn-${c.id}`}
                              >
                                <Trash2 size={13} />
                              </button>
                            )}
                            {c.result_call_id && (
                              <a
                                href={`/call-history`}
                                className="p-1.5 text-muted-foreground hover:text-emerald-400 hover:bg-emerald-500/10 rounded transition-colors"
                                title="View call result"
                                data-testid={`view-result-${c.id}`}
                              >
                                <ChevronRight size={13} />
                              </a>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
